import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useSeo } from '@/lib/seo';
import { isContentSettled, useLiveTick } from '@/lib/liveContent';
import { getCompass, COMPASS_DEMO_HREF, COMPASS_PRICING_HREF, COMPASS_ROUTE } from '@/data/compass';
import { trackEvent } from '@/lib/analytics';
import { CompassHero } from '@/components/site/compass/CompassHero';
import { CompassProblem } from '@/components/site/compass/CompassProblem';
import { CompassRhythm } from '@/components/site/compass/CompassRhythm';
import { CompassCapabilities } from '@/components/site/compass/CompassCapabilities';
import { CompassHealth } from '@/components/site/compass/CompassHealth';
import { CompassPlans } from '@/components/site/compass/CompassPlans';
import { CompassKpi } from '@/components/site/compass/CompassKpi';
import { CompassCoaching } from '@/components/site/compass/CompassCoaching';
import { CompassManagement } from '@/components/site/compass/CompassManagement';
import { CompassDevices } from '@/components/site/compass/CompassDevices';
import { CompassAudiences } from '@/components/site/compass/CompassAudiences';
import { CompassImplementation } from '@/components/site/compass/CompassImplementation';
import { CompassPricing } from '@/components/site/compass/CompassPricing';
import { CompassFaq } from '@/components/site/compass/CompassFaq';
import { CompassRelated } from '@/components/site/compass/CompassRelated';
import { CtaBand } from '@/components/site/CtaBand';

// Public Amden Compass product-marketing page. All content reads from the
// CMS-backed listing record (cms_listings / amden-compass); the Compass
// application itself is separate and untouched.
export default function CompassPage() {
  useLiveTick();
  const listingsSettled = isContentSettled('listings');
  const listing = getCompass();
  const sp = listing?.systemPage;
  const ready = listing && listing.status === 'published' && sp;

  useSeo({
    title: listing?.seo?.title || 'Amden Compass | Business Planning & Performance Platform',
    description:
      listing?.seo?.description ||
      'Connect business planning, weekly actions, KPI scorecards, coaching and performance visibility with Amden Compass.',
    canonical: listing?.seo?.canonical || `https://amden.ie${COMPASS_ROUTE}`,
    ogImage: listing?.seo?.ogImage,
    jsonLd: {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://amden.ie/' },
            { '@type': 'ListItem', position: 2, name: 'Amden Compass', item: `https://amden.ie${COMPASS_ROUTE}` },
          ],
        },
        // Verified facts only — no offers, ratings or invented metrics.
        {
          '@type': 'SoftwareApplication',
          name: 'Amden Compass',
          applicationCategory: 'BusinessApplication',
          operatingSystem: 'Web',
          description: sp?.strapline,
          url: `https://amden.ie${COMPASS_ROUTE}`,
          provider: { '@type': 'Organization', name: 'Amden Group', url: 'https://amden.ie' },
        },
      ],
    },
  });

  useEffect(() => {
    if (ready) trackEvent('compass_page_viewed');
  }, [ready]);

  if (!ready) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 bg-white px-4 text-center" data-testid="compass-loading">
        {!listingsSettled ? (
          <>
            <Loader2 className="h-6 w-6 animate-spin text-navy-900" aria-hidden="true" />
            <span className="sr-only">Loading Amden Compass</span>
          </>
        ) : (
          <>
            <h1 className="text-2xl font-extrabold tracking-tight text-navy-900">Amden Compass</h1>
            <p className="max-w-md text-sm leading-relaxed text-slate-600">
              This page is currently being updated. To learn about Amden Compass in the meantime, contact the Amden team.
            </p>
            <Link to="/contact" className="mt-2 rounded-md bg-navy-900 px-5 py-2.5 text-sm font-bold text-white hover:bg-navy-700">
              Contact Amden Group
            </Link>
          </>
        )}
      </div>
    );
  }

  return (
    <>
      <CompassHero sp={sp} />
      <CompassProblem sp={sp} />
      <CompassRhythm sp={sp} />
      <CompassCapabilities sp={sp} />
      <CompassHealth sp={sp} />
      <CompassPlans sp={sp} />
      <CompassKpi sp={sp} />
      <CompassCoaching sp={sp} />
      <CompassManagement sp={sp} />
      <CompassDevices sp={sp} />
      <CompassAudiences sp={sp} />
      <CompassImplementation sp={sp} />
      <CompassPricing sp={sp} listing={listing} />
      <CompassFaq />
      <CompassRelated sp={sp} />
      <CtaBand
        index="15"
        eyebrow="Amden Compass"
        heading="Ready to turn plans into consistent action?"
        copy="See how Compass can connect your business priorities, weekly actions, performance and coaching in one practical management system."
        primaryLabel="Book a Compass Demonstration"
        primaryHref={COMPASS_DEMO_HREF}
        secondaryLabel="Request Pricing"
        secondaryHref={COMPASS_PRICING_HREF}
      />
    </>
  );
}
